import React from 'react';
import './user-list-item.scss';
import classnames from 'classnames';
import { injectIntl, IntlShape } from 'react-intl';
import { IUserDetailsDTO } from '../../../../api/users.dao';
import Text from '../../../global/text/text';
import DefaultButton from '../../../global/default-button/default-button';
import { editText } from '../../../../constants/common-labels.constants';
import { DateTimeFormatter } from '../../../../infrastructure/implementations/date-formatter';
import * as IoC from 'typescript-ioc';

interface IProps extends IUserDetailsDTO {
    isSelected?: boolean;
    editUser?: (username: string) => void;
    searchQuery?: string;
    intl: IntlShape;
}

const UserListItem = injectIntl((props: IProps) => {
    const {
        firstName,
        lastName,
        email,
        roles,
        isSuperuser,
        isAllAccounts,
        isOpenExchangeStaffMember,
        accounts,
        username,
        isSelected,
        editUser,
        searchQuery,
        intl,
        isActive,
        insertAt,
        lastLoginAt
    } = props;
    const cellEffectClasses = classnames({
        'active': isSelected,
        'hoverable': !isSelected
    });

    const accountsColumnText = isSuperuser || isAllAccounts
        ? intl.formatMessage({
            id: 'oe.common.allAccounts',
            description: 'Label text',
            defaultMessage: 'All Accounts'
        })
        : accounts.length
            ? accounts.map(account => account.name).join(', ')
            : intl.formatMessage({
                id: 'oe.account.none',
                description: 'Label text',
                defaultMessage: 'None'
            });

    const rolesColumnText = isSuperuser
        ? intl.formatMessage({
            id: 'oe.common.superUser',
            description: 'Label text',
            defaultMessage: 'Superuser'
        })
        : roles.length
            ? roles.map(role => role.name).join(', ')
            : intl.formatMessage({
                id: 'oe.role.none',
                description: 'Label text',
                defaultMessage: 'None'
            });

    const oeStaffColumnText = isOpenExchangeStaffMember
        ? intl.formatMessage({
            id: 'oe.common.yes',
            description: 'Label text',
            defaultMessage: 'Yes'
        })
        : intl.formatMessage({
           id: 'oe.common.no',
           description: 'Label text',
           defaultMessage: 'No'
        });
    const status = isActive
        ? intl.formatMessage({
            id: 'oe.common.active',
            description: 'Label text',
            defaultMessage: 'Active'
        })
        : intl.formatMessage({
           id: 'oe.common.inactive',
           description: 'Label text',
           defaultMessage: 'Inactive'
        });

    const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

    const formatDateTime = (date: string): { date: string; time: string } => {
        if (!date) return { date: '', time: '' };
        const dateObj = new Date(date);
        // Format date as DD/MM/YYYY
        const formattedDate = dateTimeFormatter.dateToFormattedString(dateObj, 'dd/MM/yyyy');
        // Format time separately
        const formattedTime = dateTimeFormatter.dateToFormattedString(dateObj, 'HH:mm');
        return {
            date: formattedDate,
            time: formattedTime
        };
    };

    const { date: formattedCreatedDate, time: formattedCreatedTime } = formatDateTime(insertAt);
    const { date: formattedLastLoginDate, time: formattedLastLoginTime } = formatDateTime(lastLoginAt);

    return (
        <tr
            className={classnames('user-item', {'user-item__not-active': !isActive})}
            onDoubleClick={() => editUser(username)}
        >
            {[firstName, lastName].map((cellValue, index) =>
                <td key={index} className={classnames(cellEffectClasses)}>
                    <div className='user-item__column'>
                        <Text
                            className='user-item__text'
                            message={cellValue}
                            highlightValue={searchQuery}
                            withEllipsis
                            withHighlight
                        />
                    </div>
                </td>
            )}
            <td className={classnames(cellEffectClasses)}>
                <div className='user-item__column'>
                    <Text
                        className='user-item__text'
                        message={email}
                        highlightValue={searchQuery}
                        withEllipsis
                        withHighlight
                    />
                </div>
            </td>
            <td className={classnames(cellEffectClasses)}>
                <div className='user-item__column'>
                    <Text
                        className='user-item__text'
                        message={rolesColumnText}
                        highlightValue={searchQuery}
                        withEllipsis
                        withHighlight
                    />
                </div>
            </td>
            <td className={classnames(cellEffectClasses)}>
                <div className='user-item__column'>
                    <Text
                        className='user-item__text'
                        message={accountsColumnText}
                        highlightValue={searchQuery}
                        withEllipsis
                        withHighlight
                    />
                </div>
            </td>
            <td className={classnames(cellEffectClasses)}>
                <div className='user-item__column'>
                    <Text
                        className='user-item__text'
                        message={oeStaffColumnText}
                        withEllipsis
                        withHighlight
                    />
                </div>
            </td>
            <td className={classnames(cellEffectClasses)}>
                <div className='user-item__column'>
                    <Text
                        className='user-item__text '
                        message={status}
                        withEllipsis
                        withHighlight
                    />
                </div>
            </td>
            <td className={classnames(cellEffectClasses)}>
                <p>{formattedCreatedDate}</p>
                <p>{formattedCreatedTime}</p>
            </td>
            <td className={classnames(cellEffectClasses)}>
                <p>{formattedLastLoginDate}</p>
                <p>{formattedLastLoginTime}</p>
            </td>
            <td className={classnames('align-right user-item__column-edit', cellEffectClasses)}>
                <DefaultButton
                    className='visible-while-hovered'
                    onClick={() => editUser(username)}
                    modifier='slim'
                    text={editText()}
                />
            </td>
        </tr>
    );
});

export { UserListItem };
